import { blockIsShared, blockRole } from '@sites/entity-core';

import type { DenormalizedBlockEntity } from '@web/types';

/**
 * A page block whose migration failed, there or anywhere inside it. Every canvas component reads
 * the v0 shape, so such a block is kept off the canvas and written back verbatim on every save —
 * dropping it would delete it from the page for good.
 */
export type UnmigratedBlock = {
    block: DenormalizedBlockEntity;
    /** The page blocks that came before it when the page was opened, nearest first. */
    after: string[];
};

/**
 * Splits an adapted structure into what the canvas can edit and what it has to carry untouched.
 * `persisted` is the structure as it was read, before the adapter touched it — that is what gets
 * written back. A header or footer is left out of both: it keeps its slot in the site's shared
 * blocks, which a save writes back as it was.
 */
export function splitUnmigratedBlocks(
    persisted: DenormalizedBlockEntity[],
    adapted: DenormalizedBlockEntity[],
    failedIndexes: ReadonlySet<number>
): { editable: DenormalizedBlockEntity[]; unmigrated: UnmigratedBlock[] } {
    const editable: DenormalizedBlockEntity[] = [];
    const unmigrated: UnmigratedBlock[] = [];
    const before: string[] = [];

    persisted.forEach((block, i) => {
        const failed = failedIndexes.has(i);

        if (!failed) {
            editable.push(adapted[i]);
        }

        if (blockIsShared(block)) {
            return;
        }

        if (failed) {
            unmigrated.push({ block, after: [...before].reverse() });
        }

        if (typeof block.id === 'string') {
            before.push(block.id);
        }
    });

    return { editable, unmigrated };
}

/**
 * Puts the unmigrated blocks back where they were: after the nearest block that preceded them and
 * is still on the page, whatever the user moved or deleted since.
 */
export function restoreUnmigratedBlocks<T extends DenormalizedBlockEntity>(
    structure: T[],
    unmigrated: readonly UnmigratedBlock[]
): T[] {
    if (!unmigrated.length) {
        return structure;
    }

    const restored: T[] = [...structure];

    // In page order, so a block whose neighbour was unmigrated too finds it already back in place.
    for (const { block, after } of unmigrated) {
        restored.splice(insertionIndex(restored, after), 0, block as T);
    }

    return restored;
}

function insertionIndex(blocks: DenormalizedBlockEntity[], after: readonly string[]): number {
    for (const id of after) {
        const anchor = blocks.findIndex((block) => block.id === id);

        if (anchor !== -1) {
            return anchor + 1;
        }
    }

    const firstPageBlock = blocks.findIndex((block) => !blockIsShared(block));

    if (firstPageBlock !== -1) {
        return firstPageBlock;
    }

    const footer = blocks.findIndex((block) => blockIsShared(block) && blockRole(block) === 'footer');

    return footer === -1 ? blocks.length : footer;
}
